import { detailFromError } from '@eo/shared';

import { MEETING_OUTCOME_LABELS } from './constants';
import type { ApproveTaskBody, ProcessedMeeting, ProposedTask, RejectTaskBody } from './api/types';
import type { DaySplit, MeetingGroup, OverdueGroup, SetupRequired, TaskDraft } from './types';

export function meetingOutcomeLabel(meeting: ProcessedMeeting): string {
  if (meeting.outcome !== 'tasks_created') return MEETING_OUTCOME_LABELS[meeting.outcome];
  return `Created ${meeting.taskCount} task${meeting.taskCount === 1 ? '' : 's'}`;
}

export function setupRequiredFromError(err: unknown): SetupRequired {
  const detail = detailFromError(err);
  if (detail === 'USER_HAS_NO_CLIENTS') return 'clients';
  if (detail === 'CLICKUP_TOKEN_NOT_CONFIGURED') return 'token';
  return null;
}

// Working and due default to the meeting date so an untouched card still
// writes sensible dates on approve. Start is left empty on purpose: it is
// rarely what the work waits on, and a guessed one put a date nobody chose on
// every approved task.
export function toDraft(task: ProposedTask): TaskDraft {
  return {
    ...task,
    taskTypeName: null,
    parentTaskName: null,
    startDate: '',
    dueDate: task.meetingDate,
    workingDate: task.meetingDate,
    parentTaskId: null,
    comment: '',
  };
}

// Re-routing a card re-keys its member and parent lookups and drops the chosen
// parent, which belonged to the previous client's work. The Client field value
// is cleared so the service resolves it from the name against ClickUp's live
// options rather than carrying the old client's.
export function clientChangePatch(client: { id: string; name: string }): Partial<TaskDraft> {
  return {
    client: client.name,
    clientId: client.id,
    clientFieldValue: null,
    parentTaskId: null,
    parentTaskName: null,
  };
}

// Overdue is measured against today, never against the selected date — a CFO
// standing on an old day must still see the whole backlog, and that day's own
// tasks belong to both halves.
export function splitByDay(drafts: TaskDraft[], boardDate: string, today: string): DaySplit {
  return {
    onTheDay: drafts.filter((d) => d.meetingDate === boardDate),
    overdue: drafts.filter((d) => d.meetingDate < today),
  };
}

// One group per meeting day, oldest first: the head of the list is the day
// closest to being purged unreviewed.
export function groupOverdueByDate(drafts: TaskDraft[], today: string): OverdueGroup[] {
  const byDate = new Map<string, TaskDraft[]>();
  for (const draft of drafts) {
    const bucket = byDate.get(draft.meetingDate) ?? [];
    bucket.push(draft);
    byDate.set(draft.meetingDate, bucket);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, groupDrafts]) => ({
      date,
      daysWaiting: daysBetween(date, today),
      drafts: groupDrafts,
    }));
}

// The days the picker dots. Today is never one of them: its work is not late.
export function overdueDates(drafts: TaskDraft[], today: string): string[] {
  return [...new Set(drafts.filter((d) => d.meetingDate < today).map((d) => d.meetingDate))].sort();
}

function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / 86_400_000);
}

// Recall is organised by conversation, so the meeting is the grouping axis.
// Client is a routing field, not a spatial one — making it the axis is what
// forced a horizontally scrolling board. Oldest meeting leads: it is closest
// to being purged unreviewed.
export function groupIntoMeetings(drafts: TaskDraft[]): MeetingGroup[] {
  const byMeeting = new Map<string, MeetingGroup>();

  for (const draft of drafts) {
    const key = JSON.stringify([draft.meetingDate, draft.meetingTitle]);
    const group = byMeeting.get(key) ?? {
      meetingTitle: draft.meetingTitle,
      meetingDate: draft.meetingDate,
      drafts: [],
    };
    group.drafts.push(draft);
    byMeeting.set(key, group);
  }

  return [...byMeeting.values()].sort(
    (a, b) =>
      a.meetingDate.localeCompare(b.meetingDate) || a.meetingTitle.localeCompare(b.meetingTitle),
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Fixed month names rather than toLocaleDateString: the board is read by one
// team and a locale-dependent label makes the tests date-machine-dependent.
export function shortDate(iso: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!parts) return '';
  return `${MONTHS[Number(parts[2]) - 1]} ${Number(parts[3])}`;
}

// The right side: how big the commitment is and who carries it. On a small
// team a name is what people recognise, but a long one would push the rail
// wider than the content beside it — so names truncate, and beyond two people
// the rest become a count.
export const MAX_ASSIGNEE_CHIPS = 2;
export const MAX_ASSIGNEE_NAME_CHARS = 12;

function assigneeName(assignee: { username: string; initials: string | null }): string {
  const name = assignee.username.trim() || assignee.initials || '';
  if (name.length <= MAX_ASSIGNEE_NAME_CHARS) return name;
  return `${name.slice(0, MAX_ASSIGNEE_NAME_CHARS - 1).trimEnd()}…`;
}

// Every place that names a person uses this, so the card and the editor
// cannot describe the same assignee differently.
export function assigneeChips(
  assignees: TaskDraft['assignees'],
): Array<{ id: number; name: string; color: string | null }> {
  // Colour is only carried when there is someone to be told apart from. One
  // assignee needs no distinguishing, and a lone coloured chip would read as
  // a status the board does not have.
  const distinguish = assignees.length > 1;
  return assignees.map((a) => ({
    id: a.id,
    name: assigneeName(a),
    color: distinguish ? a.color : null,
  }));
}

export function cardRail(draft: TaskDraft): {
  estimate: string | null;
  people: Array<{ name: string; color: string | null }>;
  overflow: number;
} {
  const everyone = assigneeChips(draft.assignees);
  return {
    estimate: draft.timeEstimateMinutes === null ? null : `${draft.timeEstimateMinutes} min`,
    people: everyone.slice(0, MAX_ASSIGNEE_CHIPS).map(({ name, color }) => ({ name, color })),
    overflow: Math.max(everyone.length - MAX_ASSIGNEE_CHIPS, 0),
  };
}

// A client is only named in the heading when every task in the meeting shares
// it; a mixed meeting leaves each card to say its own.
export function meetingHeading(group: MeetingGroup): { title: string; meta: string } {
  const clients = new Set(group.drafts.map((d) => d.client));
  const sharedClient = clients.size === 1 ? group.drafts[0].client : undefined;
  const count = `${group.drafts.length} task${group.drafts.length === 1 ? '' : 's'}`;

  return {
    title: group.meetingTitle || 'Untitled meeting',
    meta: [sharedClient, shortDate(group.meetingDate), count].filter(Boolean).join(' · '),
  };
}

// The pipeline drops anything undecided at 31 days, so the number that matters
// on the backlog is not how long it has waited but how long is left.
export const REVIEW_WINDOW_DAYS = 31;

export function daysUntilDropped(daysWaiting: number): number {
  return Math.max(REVIEW_WINDOW_DAYS - daysWaiting, 0);
}

// Board shortcuts must never steal a keystroke from a control that has focus —
// someone typing a task name, or tabbing to a button and pressing Enter on it.
export function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.closest('[role="dialog"]') !== null) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(target.tagName);
}

export function toApproveBody(draft: TaskDraft, actionDate: string): ApproveTaskBody {
  return {
    actionDate,
    task: {
      taskId: draft.taskId,
      taskName: draft.taskName,
      context: draft.context,
      client: draft.client,
      clientFieldValue: draft.clientFieldValue,
      startDate: draft.startDate || null,
      dueDate: draft.dueDate || null,
      workingDate: draft.workingDate || null,
      taskTypeId: draft.taskTypeId,
      timeEstimateMinutes: draft.timeEstimateMinutes,
      priority: draft.priority,
      parentTaskId: draft.parentTaskId,
      assigneeIds: draft.assignees.map((a) => a.id),
      comment: draft.comment,
      meetingTitle: draft.meetingTitle,
    },
  };
}

export function toRejectBody(draft: TaskDraft, actionDate: string): RejectTaskBody {
  return {
    actionDate,
    taskId: draft.taskId,
    taskName: draft.taskName,
    client: draft.client,
    meetingTitle: draft.meetingTitle,
  };
}

// The undo of an approved task comes back refused when someone is already
// working on it; the board asks the CFO instead of treating it as a failure.
// Read off the action itself, because unwrapping keeps only the message.
export function undoInProgressMessage(result: unknown): string | null {
  const error = (result as { payload?: { errors?: Array<{ detail?: string; message?: string }> } })
    ?.payload?.errors?.[0];
  return error?.detail === 'TASK_IN_PROGRESS' ? (error.message ?? null) : null;
}

export function failedAttachmentsNotice(names: string[]): string | null {
  if (names.length === 0) return null;
  return `Approved, but ${names.join(', ')} did not reach ClickUp. Add ${names.length === 1 ? 'it' : 'them'} to the task there.`;
}
